import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Button,
  Chip,
  Typography,
  Tabs,
  Tab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  Snackbar,
  Alert,
  Skeleton,
} from '@mui/material';
import { Add, NightsStay, WbSunny, Check, Close as CloseIcon } from '@mui/icons-material';

import hrPlusService from '../../services/hrPlusService';
import hrService, { EmployeeListItem } from '../../services/hrService';
import {
  PageHeader,
  FilterBar,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';

// ---------------------------------------------------------------------------
// Shifts & Attendance+ — shift master, roster assignment and attendance
// regularization, all on Payroll+ (migration 063, hrPayrollPlus model).
// ---------------------------------------------------------------------------

type RegularizationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

interface Shift {
  id: number;
  name: string;
  start_time: string | null;
  end_time: string | null;
  break_minutes: number;
  is_night: number | boolean;
  is_active: number | boolean;
}

interface RosterAssignment {
  id: number;
  employee_id: number;
  employee_name: string;
  shift_id: number;
  shift_name: string;
  start_time: string | null;
  end_time: string | null;
  from_date: string;
  to_date: string | null;
}

interface Regularization {
  id: number;
  employee_id: number;
  employee_name: string;
  attendance_date: string;
  requested_check_in: string | null;
  requested_check_out: string | null;
  reason: string | null;
  status: RegularizationStatus;
}

const isTruthy = (v: any): boolean => v === 1 || v === true || v === '1';

const fmtDate = (v: any): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtTime = (v: any): string => (v ? String(v).slice(0, 5) : '—');

const employeeLabel = (e: EmployeeListItem): string => `${e.first_name} ${e.last_name || ''} (${e.employee_code})`.trim();

const REG_STATUS_TONE: Record<RegularizationStatus, StatusTone> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

// ---------------------------------------------------------------------------
// New shift dialog
// ---------------------------------------------------------------------------

const NewShiftDialog: React.FC<{
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; startTime: string; endTime: string; breakMinutes: string; isNight: boolean }) => void;
}> = ({ open, saving, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [breakMinutes, setBreakMinutes] = useState('60');
  const [isNight, setIsNight] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(''); setStartTime(''); setEndTime(''); setBreakMinutes('60'); setIsNight(false); setError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!name.trim()) { setError('Shift name is required'); return; }
    onSubmit({ name: name.trim(), startTime, endTime, breakMinutes, isNight });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>New Shift</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.25 }}>
          <TextField label="Shift Name" required fullWidth size="small" value={name} error={!!error} helperText={error || ' '} onChange={(e) => { setName(e.target.value); if (e.target.value.trim()) setError(null); }} />
          <Stack direction="row" spacing={2}>
            <TextField label="Start Time" type="time" fullWidth size="small" InputLabelProps={{ shrink: true }} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            <TextField label="End Time" type="time" fullWidth size="small" InputLabelProps={{ shrink: true }} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </Stack>
          <TextField label="Break (minutes)" type="number" fullWidth size="small" value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} />
          <FormControlLabel control={<Switch checked={isNight} onChange={(e) => setIsNight(e.target.checked)} />} label="Night Shift" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Create Shift'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Assign roster dialog
// ---------------------------------------------------------------------------

const AssignRosterDialog: React.FC<{
  open: boolean;
  saving: boolean;
  employees: EmployeeListItem[];
  shifts: Shift[];
  onClose: () => void;
  onSubmit: (data: { employeeId: number; shiftId: number; fromDate: string; toDate: string }) => void;
}> = ({ open, saving, employees, shifts, onClose, onSubmit }) => {
  const [employee, setEmployee] = useState<EmployeeListItem | null>(null);
  const [shift, setShift] = useState<Shift | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setEmployee(null); setShift(null); setFromDate(''); setToDate(''); setError(null); }
  }, [open]);

  const handleSubmit = () => {
    if (!employee || !shift || !fromDate) { setError('Employee, shift and from-date are required'); return; }
    onSubmit({ employeeId: employee.id, shiftId: shift.id, fromDate, toDate });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Assign Roster</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.25 }}>
          <Autocomplete
            size="small"
            options={employees}
            value={employee}
            onChange={(_, v) => setEmployee(v)}
            getOptionLabel={employeeLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Employee" required />}
          />
          <Autocomplete
            size="small"
            options={shifts}
            value={shift}
            onChange={(_, v) => setShift(v)}
            getOptionLabel={(s) => s.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Shift" required />}
          />
          <Stack direction="row" spacing={2}>
            <TextField label="From Date" type="date" fullWidth size="small" required InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <TextField label="To Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </Stack>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Assign'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// New regularization dialog
// ---------------------------------------------------------------------------

const NewRegularizationDialog: React.FC<{
  open: boolean;
  saving: boolean;
  employees: EmployeeListItem[];
  onClose: () => void;
  onSubmit: (data: { employeeId: number; attendanceDate: string; requestedCheckIn: string; requestedCheckOut: string; reason: string }) => void;
}> = ({ open, saving, employees, onClose, onSubmit }) => {
  const [employee, setEmployee] = useState<EmployeeListItem | null>(null);
  const [attendanceDate, setAttendanceDate] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setEmployee(null); setAttendanceDate(''); setCheckIn(''); setCheckOut(''); setReason(''); setError(null); }
  }, [open]);

  const handleSubmit = () => {
    if (!employee || !attendanceDate) { setError('Employee and date are required'); return; }
    if (!checkIn && !checkOut) { setError('Provide at least a requested check-in or check-out time'); return; }
    onSubmit({ employeeId: employee.id, attendanceDate, requestedCheckIn: checkIn, requestedCheckOut: checkOut, reason });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>New Regularization</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.25 }}>
          <Autocomplete
            size="small"
            options={employees}
            value={employee}
            onChange={(_, v) => setEmployee(v)}
            getOptionLabel={employeeLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Employee" required />}
          />
          <TextField label="Attendance Date" type="date" fullWidth size="small" required InputLabelProps={{ shrink: true }} value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField label="Requested Check-In" type="time" fullWidth size="small" InputLabelProps={{ shrink: true }} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
            <TextField label="Requested Check-Out" type="time" fullWidth size="small" InputLabelProps={{ shrink: true }} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
          </Stack>
          <TextField label="Reason" fullWidth size="small" multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Submit'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TAB_LABELS = ['Shifts & Roster', 'Regularizations'];

const ShiftsAttendancePlusPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [roster, setRoster] = useState<RosterAssignment[]>([]);
  const [regularizations, setRegularizations] = useState<Regularization[]>([]);

  const [regStatusFilter, setRegStatusFilter] = useState<RegularizationStatus | 'ALL'>('PENDING');

  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftSaving, setShiftSaving] = useState(false);

  const [rosterDialogOpen, setRosterDialogOpen] = useState(false);
  const [rosterSaving, setRosterSaving] = useState(false);

  const [regDialogOpen, setRegDialogOpen] = useState(false);
  const [regSaving, setRegSaving] = useState(false);
  const [regBusyId, setRegBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [empRes, shiftRes, rosterRes, regRes] = await Promise.all([
        hrService.getEmployees({ limit: 100, status: 'ACTIVE' }),
        hrPlusService.getShifts(),
        hrPlusService.getRoster(),
        hrPlusService.getRegularizations(),
      ]);
      setEmployees(empRes.employees || []);
      setShifts(shiftRes.shifts || []);
      setRoster(rosterRes.roster || []);
      setRegularizations(regRes.regularizations || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load shifts & attendance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reloadShifts = useCallback(async () => {
    try {
      const r = await hrPlusService.getShifts();
      setShifts(r.shifts || []);
    } catch { /* keep previous list */ }
  }, []);

  const reloadRoster = useCallback(async () => {
    try {
      const r = await hrPlusService.getRoster();
      setRoster(r.roster || []);
    } catch { /* keep previous list */ }
  }, []);

  const reloadRegularizations = useCallback(async () => {
    try {
      const r = await hrPlusService.getRegularizations();
      setRegularizations(r.regularizations || []);
    } catch { /* keep previous list */ }
  }, []);

  const handleCreateShift = async (data: { name: string; startTime: string; endTime: string; breakMinutes: string; isNight: boolean }) => {
    setShiftSaving(true);
    try {
      await hrPlusService.createShift({
        name: data.name,
        startTime: data.startTime || undefined,
        endTime: data.endTime || undefined,
        breakMinutes: data.breakMinutes === '' ? undefined : Number(data.breakMinutes),
        isNight: data.isNight,
      });
      setToast({ msg: 'Shift created', severity: 'success' });
      setShiftDialogOpen(false);
      await reloadShifts();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to create shift', severity: 'error' });
    } finally {
      setShiftSaving(false);
    }
  };

  const handleAssignRoster = async (data: { employeeId: number; shiftId: number; fromDate: string; toDate: string }) => {
    setRosterSaving(true);
    try {
      await hrPlusService.assignRoster({
        employeeId: data.employeeId,
        shiftId: data.shiftId,
        fromDate: data.fromDate,
        toDate: data.toDate || undefined,
      });
      setToast({ msg: 'Roster assigned', severity: 'success' });
      setRosterDialogOpen(false);
      await reloadRoster();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to assign roster', severity: 'error' });
    } finally {
      setRosterSaving(false);
    }
  };

  const handleCreateRegularization = async (data: { employeeId: number; attendanceDate: string; requestedCheckIn: string; requestedCheckOut: string; reason: string }) => {
    setRegSaving(true);
    try {
      await hrPlusService.createRegularization(data.employeeId, {
        attendanceDate: data.attendanceDate,
        requestedCheckIn: data.requestedCheckIn || undefined,
        requestedCheckOut: data.requestedCheckOut || undefined,
        reason: data.reason || undefined,
      });
      setToast({ msg: 'Regularization request submitted', severity: 'success' });
      setRegDialogOpen(false);
      await reloadRegularizations();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to submit regularization', severity: 'error' });
    } finally {
      setRegSaving(false);
    }
  };

  const handleRegularizationStatus = async (id: number, status: 'APPROVED' | 'REJECTED') => {
    setRegBusyId(id);
    try {
      await hrPlusService.setRegularizationStatus(id, status);
      setToast({ msg: `Regularization ${status.toLowerCase()}`, severity: 'success' });
      await reloadRegularizations();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to update regularization', severity: 'error' });
    } finally {
      setRegBusyId(null);
    }
  };

  const filteredRegularizations = useMemo(
    () => (regStatusFilter === 'ALL' ? regularizations : regularizations.filter((r) => r.status === regStatusFilter)),
    [regularizations, regStatusFilter]
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={220} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={44} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader title="Shifts & Attendance+" subtitle="Shift master, roster assignment and attendance regularization" />
        <TableSurface><ErrorState message={error} onRetry={load} /></TableSurface>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title="Shifts & Attendance+" subtitle="Shift master, roster assignment and attendance regularization" />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2.5, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}
      >
        {TAB_LABELS.map((t) => <Tab key={t} label={t} />)}
      </Tabs>

      {/* ---------------- Shifts & Roster ---------------- */}
      {tab === 0 && (
        <Grid container spacing={2.5}>
          <Grid size={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Shifts</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setShiftDialogOpen(true)}>New Shift</Button>
            </Stack>
            <TableSurface>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Name</TableCell>
                      <TableCell sx={headerCellSx}>Start</TableCell>
                      <TableCell sx={headerCellSx}>End</TableCell>
                      <TableCell sx={headerCellSx} align="right">Break (min)</TableCell>
                      <TableCell sx={headerCellSx}>Night</TableCell>
                      <TableCell sx={headerCellSx}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shifts.length === 0 ? (
                      <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><TableEmptyState title="No shifts defined" hint="Create a shift to start building rosters." /></TableCell></TableRow>
                    ) : (
                      shifts.map((s) => (
                        <TableRow key={s.id} hover sx={bodyRowSx}>
                          <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                          <TableCell>{fmtTime(s.start_time)}</TableCell>
                          <TableCell>{fmtTime(s.end_time)}</TableCell>
                          <TableCell align="right">{s.break_minutes}</TableCell>
                          <TableCell>{isTruthy(s.is_night) ? <NightsStay fontSize="small" color="action" /> : <WbSunny fontSize="small" color="action" />}</TableCell>
                          <TableCell><StatusDot label={isTruthy(s.is_active) ? 'Active' : 'Inactive'} tone={isTruthy(s.is_active) ? 'success' : 'neutral'} /></TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          </Grid>

          <Grid size={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Roster</Typography>
              <Button variant="contained" startIcon={<Add />} disabled={shifts.length === 0} onClick={() => setRosterDialogOpen(true)}>Assign Roster</Button>
            </Stack>
            <TableSurface>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Employee</TableCell>
                      <TableCell sx={headerCellSx}>Shift</TableCell>
                      <TableCell sx={headerCellSx}>Timing</TableCell>
                      <TableCell sx={headerCellSx}>From</TableCell>
                      <TableCell sx={headerCellSx}>To</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roster.length === 0 ? (
                      <TableRow><TableCell colSpan={5} sx={{ border: 0 }}><TableEmptyState title="No roster assignments" hint="Assign employees to shifts to build the roster." /></TableCell></TableRow>
                    ) : (
                      roster.map((r) => (
                        <TableRow key={r.id} hover sx={bodyRowSx}>
                          <TableCell sx={{ fontWeight: 600 }}>{r.employee_name}</TableCell>
                          <TableCell>{r.shift_name}</TableCell>
                          <TableCell>{fmtTime(r.start_time)} – {fmtTime(r.end_time)}</TableCell>
                          <TableCell>{fmtDate(r.from_date)}</TableCell>
                          <TableCell>{r.to_date ? fmtDate(r.to_date) : 'Ongoing'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          </Grid>
        </Grid>
      )}

      {/* ---------------- Regularizations ---------------- */}
      {tab === 1 && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => setRegDialogOpen(true)}>New Regularization</Button>
          </Stack>

          <FilterBar>
            {(['ALL', 'PENDING', 'APPROVED', 'REJECTED'] as const).map((s) => (
              <Chip
                key={s}
                label={s === 'ALL' ? `All (${regularizations.length})` : `${s} (${regularizations.filter((x) => x.status === s).length})`}
                color={regStatusFilter === s ? 'primary' : 'default'}
                variant={regStatusFilter === s ? 'filled' : 'outlined'}
                onClick={() => setRegStatusFilter(s)}
              />
            ))}
          </FilterBar>

          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Employee</TableCell>
                    <TableCell sx={headerCellSx}>Date</TableCell>
                    <TableCell sx={headerCellSx}>Requested In</TableCell>
                    <TableCell sx={headerCellSx}>Requested Out</TableCell>
                    <TableCell sx={headerCellSx}>Reason</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRegularizations.length === 0 ? (
                    <TableRow><TableCell colSpan={7} sx={{ border: 0 }}><TableEmptyState title="No regularization requests" hint="Submit one when attendance needs a manual correction." /></TableCell></TableRow>
                  ) : (
                    filteredRegularizations.map((r) => (
                      <TableRow key={r.id} hover sx={bodyRowSx}>
                        <TableCell sx={{ fontWeight: 600 }}>{r.employee_name}</TableCell>
                        <TableCell>{fmtDate(r.attendance_date)}</TableCell>
                        <TableCell>{fmtTime(r.requested_check_in)}</TableCell>
                        <TableCell>{fmtTime(r.requested_check_out)}</TableCell>
                        <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason || undefined}>{r.reason || '—'}</TableCell>
                        <TableCell><StatusDot label={r.status} tone={REG_STATUS_TONE[r.status]} /></TableCell>
                        <TableCell>
                          {r.status === 'PENDING' ? (
                            <Stack direction="row" spacing={0.5}>
                              <Button size="small" color="success" startIcon={<Check />} disabled={regBusyId === r.id} onClick={() => handleRegularizationStatus(r.id, 'APPROVED')}>Approve</Button>
                              <Button size="small" color="error" startIcon={<CloseIcon />} disabled={regBusyId === r.id} onClick={() => handleRegularizationStatus(r.id, 'REJECTED')}>Reject</Button>
                            </Stack>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Box>
      )}

      <NewShiftDialog open={shiftDialogOpen} saving={shiftSaving} onClose={() => setShiftDialogOpen(false)} onSubmit={handleCreateShift} />

      <AssignRosterDialog
        open={rosterDialogOpen}
        saving={rosterSaving}
        employees={employees}
        shifts={shifts}
        onClose={() => setRosterDialogOpen(false)}
        onSubmit={handleAssignRoster}
      />

      <NewRegularizationDialog
        open={regDialogOpen}
        saving={regSaving}
        employees={employees}
        onClose={() => setRegDialogOpen(false)}
        onSubmit={handleCreateRegularization}
      />

      <Snackbar open={!!toast} autoHideDuration={3200} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default ShiftsAttendancePlusPage;
